import {
    ActionsGroup,
    NumberFormat,
    Page,
    PageSection,
    ResponsiveTable,
    ResponsiveTableBody,
    ResponsiveTableCell,
    ResponsiveTableFooter,
    ResponsiveTableHeaderCell,
    ResponsiveTableHeaderRow,
    ResponsiveTablePagination,
    ResponsiveTableRow,
    ResponsiveTableRowActions,
    SearchAndFiltersLayout,
    SearchInput,
    SearchSummary,
    Button
} from '@crx-dev/hss-react-components';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { usePaginationInUrl } from '../../../Infrastructure/pageState/usePaginationInUrl';
import { useQuery } from 'react-query';
import { PaginatedResponse } from '../../Common/types/PaginatedResponse';
import { makeRequest } from '@crx-dev/hss-react-infrastructure';
import { makeReturnUrl } from '../../../Infrastructure/pageState/useMakeReturnUrl';
import { ReturnedRegisterSummary } from './types/ReturnedRegisterSummary';

function ReturnedRegistersList() {
    const history = useHistory();

    const {
        pageState: { page, pageSize, sortField, sortDirection, search },
        actions: { setPage, setSort, setPageSize, setSearch }
    } = usePaginationInUrl({
        sortField: 'Balance',
        sortDirection: 'desc'
    });

    const { data, isLoading, isFetching } = useQuery<PaginatedResponse<ReturnedRegisterSummary[]>>(
        ['returned-register-list', page, pageSize, sortField, sortDirection, search],
        () =>
            makeRequest({
                method: 'GET',
                path: '/api/v1/ReturnedDrugRegister',
                data: {
                    page,
                    pageSize,
                    sortField,
                    sortDirection,
                    search
                }
            }),
        {
            keepPreviousData: true
        }
    );

    const handleSearch = useCallback((value: string) => {
        setSearch(value);
    }, []);

    const handleViewReturnRegister = useCallback((registerId: string) => {
        let returnUrl = makeReturnUrl();
        history.push(`/returned-registers/${registerId}?returnUrl=${returnUrl}`);
    }, []);

    return (
        <Page>
            <Page.Header
                title="Returned Drug Registers"
                pageActions={
                    <ActionsGroup>
                        <Button id="record-event" onClick={() => history.push('/returned-registers/add')}>
                            Add New Register
                        </Button>
                    </ActionsGroup>
                }
            />
            <PageSection>
                <SearchAndFiltersLayout
                    search={
                        <SearchInput
                            name="search-field"
                            onSearch={handleSearch}
                            placeholder="Search drugs"
                            defaultValue={search}
                        />
                    }
                />

                <SearchSummary
                    filtersConfig={[]}
                    filterValues={{}}
                    onFiltersChange={() => {}}
                    searchTerm={search}
                    onSearchChange={handleSearch}
                />

                <ResponsiveTable
                    id="ReturnedRegisterSummaries"
                    collapseAt={800}
                    isLoading={isLoading}
                    isUpdating={isFetching}
                    sortParams={{ field: sortField, direction: sortDirection }}
                    onSort={setSort}
                >
                    <ResponsiveTableHeaderRow>
                        <ResponsiveTableHeaderCell sortField="ProductName">Product</ResponsiveTableHeaderCell>
                        <ResponsiveTableHeaderCell sortField="Balance">Returned Quantity</ResponsiveTableHeaderCell>
                        <ResponsiveTableHeaderCell />
                    </ResponsiveTableHeaderRow>
                    <ResponsiveTableBody>
                        {data &&
                            data.payload.map(item => (
                                <ResponsiveTableRow key={item.returnedRegisterId}>
                                    <ResponsiveTableCell promoteToHeaderOnMobile>
                                        {item.productName}
                                    </ResponsiveTableCell>
                                    <ResponsiveTableCell>
                                        <NumberFormat value={item.balance} />
                                    </ResponsiveTableCell>
                                    <ResponsiveTableRowActions>
                                        <Button
                                            id={`view-${item.returnedRegisterId}`}
                                            size="small"
                                            onClick={() => handleViewReturnRegister(item.returnedRegisterId)}
                                            variant="secondary"
                                        >
                                            View Register
                                        </Button>
                                    </ResponsiveTableRowActions>
                                </ResponsiveTableRow>
                            ))}
                    </ResponsiveTableBody>
                    <ResponsiveTableFooter>
                        <ResponsiveTablePagination
                            page={page}
                            rowsPerPage={pageSize}
                            totalResults={data?.count ?? 0}
                            onPageChange={setPage}
                            onRowsPerPageChange={setPageSize}
                            showRowsPerPageSelect
                        />
                    </ResponsiveTableFooter>
                </ResponsiveTable>
            </PageSection>
        </Page>
    );
}

export default ReturnedRegistersList;
